import React from "react";
import { useExcursion } from "../api";
import type { ExcursionParams, ExcursionGroupParams } from "../../api/excursions/Excursion.typed";

/** Templates only — does not subscribe to loading toggles (fewer re-renders on planning). */
export interface ExcursionTemplatesContextValue {
  excursionTemplates: ExcursionParams[];
  groupTemplates: ExcursionGroupParams[];
  reload: () => Promise<void>;
}

export interface ExcursionsDataContextValue extends ExcursionTemplatesContextValue {
  loading: boolean;
}

const ExcursionTemplatesContext = React.createContext<ExcursionTemplatesContextValue | undefined>(
  undefined,
);
const ExcursionTemplatesLoadingContext = React.createContext<boolean>(false);

export function ExcursionsDataProvider({ children }: { children: React.ReactNode }) {
  const { listExcursionParams, listExcursionGroupParams } = useExcursion();
  const [excursionTemplates, setExcursionTemplates] = React.useState<ExcursionParams[]>([]);
  const [groupTemplates, setGroupTemplates] = React.useState<ExcursionGroupParams[]>([]);
  const [loading, setLoading] = React.useState(false);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const [excList, grpList] = await Promise.all([
        listExcursionParams() ?? [],
        listExcursionGroupParams() ?? [],
      ]);
      setExcursionTemplates(Array.isArray(excList) ? excList : []);
      setGroupTemplates(Array.isArray(grpList) ? grpList : []);
    } finally {
      setLoading(false);
    }
  }, [listExcursionParams, listExcursionGroupParams]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const templatesValue = React.useMemo<ExcursionTemplatesContextValue>(
    () => ({ excursionTemplates, groupTemplates, reload }),
    [excursionTemplates, groupTemplates, reload],
  );

  return (
    <ExcursionTemplatesContext.Provider value={templatesValue}>
      <ExcursionTemplatesLoadingContext.Provider value={loading}>
        {children}
      </ExcursionTemplatesLoadingContext.Provider>
    </ExcursionTemplatesContext.Provider>
  );
}

export function useExcursionTemplates(): ExcursionTemplatesContextValue {
  const ctx = React.useContext(ExcursionTemplatesContext);
  if (!ctx) throw new Error("useExcursionTemplates must be used within ExcursionsDataProvider");

  return ctx;
}

const emptyTemplates: ExcursionTemplatesContextValue = {
  excursionTemplates: [],
  groupTemplates: [],
  reload: async () => {},
};

export function useOptionalExcursionTemplates(): ExcursionTemplatesContextValue {
  return React.useContext(ExcursionTemplatesContext) ?? emptyTemplates;
}

/** Full data including loading — use on template admin pages; planning should prefer useExcursionTemplates. */
export function useExcursionsData(): ExcursionsDataContextValue {
  const templates = useExcursionTemplates();
  const loading = React.useContext(ExcursionTemplatesLoadingContext);
  return React.useMemo(() => ({ ...templates, loading }), [templates, loading]);
}
